import { BankingPaymentRail, ELinxConnectionMethod, PaymentMethodType, withStyles } from 'vopay';
import styles from './ConvenienceFeeBreakdown.module.scss';
import { useTranslation } from 'react-i18next';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import { paymentMethodToElinxConnectionMethodMap } from '@/utils';
import { getBankingRailDescription } from '@/utils/bankingRailMapping';
import { usePaylink, Flow } from '@/providers/Paylink.provider';

const cn = withStyles(styles);

export default function Fee({
  connectionMethod,
  paymentMethodType,
  province,
  bankingPaymentRail,
  className,
  showDeliveryTime = true,
  compact = false,
}: {
  province?: string;
  bankingPaymentRail?: BankingPaymentRail;
  showDeliveryTime?: boolean;
  className?: string;
  /** The eLinx redesign's Body/13 caption: inherits the surrounding text style, `·` separator, small Free badge */
  compact?: boolean;
} & (
  | {
      connectionMethod: ELinxConnectionMethod;
      paymentMethodType?: never;
    }
  | {
      connectionMethod?: never;
      paymentMethodType: PaymentMethodType;
    }
)) {
  const { t: tConvenience } = useTranslation('translations', { keyPrefix: 'ConvenienceFee' });
  const { t: tBankingRail } = useTranslation('translations', { keyPrefix: 'BankingRail' });
  const { getProcessingFee, getLowestAndHighestFees, showFees } = useConvenienceFeesProvider();
  const { flow } = usePaylink();

  const method = connectionMethod || paymentMethodToElinxConnectionMethodMap[paymentMethodType];
  const isNoTransactionFlow = flow === Flow.NONE || flow === Flow.CONNECT;

  const buildFeeDisplay = (
    processingFee: string,
    transactionFee: string,
    processingFeeNumeric: number,
    transactionFeeNumeric: number,
  ) => {
    if (processingFeeNumeric !== 0 && transactionFeeNumeric === 0) return processingFee;
    if (processingFeeNumeric === 0 && transactionFeeNumeric !== 0) return transactionFee;

    return `${processingFee} + ${transactionFee}`;
  };

  const getLowestDisplay = (lowest: {
    processingFee: string;
    transactionFee: string;
    processingFeeNumeric: number;
    transactionFeeNumeric: number;
  }) => {
    if (lowest.processingFeeNumeric === 0 && lowest.transactionFeeNumeric === 0) {
      return tConvenience('freeLowerCase', { defaultValue: 'free' });
    }

    return buildFeeDisplay(
      lowest.processingFee,
      lowest.transactionFee,
      lowest.processingFeeNumeric,
      lowest.transactionFeeNumeric,
    );
  };

  const getHighestDisplay = (highest: {
    processingFee: string;
    transactionFee: string;
    processingFeeNumeric: number;
    transactionFeeNumeric: number;
  }) => {
    return buildFeeDisplay(
      highest.processingFee,
      highest.transactionFee,
      highest.processingFeeNumeric,
      highest.transactionFeeNumeric,
    );
  };

  // For flows 0 and 5, use fee range logic
  if (isNoTransactionFlow && showFees) {
    const feeRange = getLowestAndHighestFees(method);

    if (!feeRange.lowest || !feeRange.highest) {
      return (
        <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
          {tConvenience('noFees', { defaultValue: 'No fees' })}
        </div>
      );
    }

    const { lowest, highest } = feeRange;

    // Check if processing fee and transaction fee vary independently
    const processingFeeVaries = lowest.processingFeeNumeric !== highest.processingFeeNumeric;
    const transactionFeeVaries = lowest.transactionFeeNumeric !== highest.transactionFeeNumeric;

    // If neither varies, they're the same - display as fixed fee
    if (!processingFeeVaries && !transactionFeeVaries) {
      const { processingFee, transactionFee, processingFeeNumeric, transactionFeeNumeric } = lowest;

      if (processingFeeNumeric === 0 && transactionFeeNumeric === 0) {
        return (
          <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
            {tConvenience('noFees', { defaultValue: 'No fees' })}
          </div>
        );
      }

      let feeDisplay = '';

      if (processingFeeNumeric !== 0 && transactionFeeNumeric === 0) {
        feeDisplay = processingFee;
      } else if (processingFeeNumeric === 0 && transactionFeeNumeric !== 0) {
        feeDisplay = transactionFee;
      } else {
        feeDisplay = `${processingFee} + ${transactionFee}`;
      }

      return (
        <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
          {tConvenience('feeAmount', {
            amount: feeDisplay,
            defaultValue: `Fee: ${feeDisplay} per transaction`,
          })}
        </div>
      );
    }

    // Display fee range based on what varies
    // For other payment methods, handle different variation scenarios

    // Case 1: Both processing and transaction fees vary
    if (processingFeeVaries && transactionFeeVaries) {
      return (
        <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
          {tConvenience(method === 'bank' ? 'feesVaryByPaymentRail' : 'feesVary', {
            lowestFee: getLowestDisplay(lowest),
            highestFee: getHighestDisplay(highest),
            defaultValue:
              method === 'bank'
                ? `Fees vary by payment rail (typically ${getLowestDisplay(lowest)} - ${getHighestDisplay(highest)} per transaction)`
                : `Fees vary (typically ${getLowestDisplay(lowest)} - ${getHighestDisplay(highest)} per transaction)`,
          })}
        </div>
      );
    }

    // Case 2: Only processing fee varies, transaction fee is fixed
    if (processingFeeVaries && !transactionFeeVaries) {
      const fixedFee = lowest.transactionFeeNumeric !== 0 ? ` + ${lowest.transactionFee}` : '';

      const lowestDisplay =
        lowest.processingFeeNumeric === 0
          ? lowest.transactionFeeNumeric === 0
            ? tConvenience('freeLowerCase', { defaultValue: 'free' })
            : lowest.transactionFee
          : `${lowest.processingFee}${fixedFee}`;

      const highestDisplay = `${highest.processingFee}${fixedFee}`;

      return (
        <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
          {tConvenience(method === 'bank' ? 'feesVaryByPaymentRail' : 'feesVary', {
            lowestFee: lowestDisplay,
            highestFee: highestDisplay,
            defaultValue:
              method === 'bank'
                ? `Fees vary by payment rail (typically ${lowestDisplay} - ${highestDisplay} per transaction)`
                : `Fees vary (typically ${lowestDisplay} - ${highestDisplay} per transaction)`,
          })}
        </div>
      );
    }

    // Case 3: Only transaction fee varies, processing fee is fixed
    if (!processingFeeVaries && transactionFeeVaries) {
      const fixedFee = lowest.processingFeeNumeric !== 0 ? `${lowest.processingFee} + ` : '';

      const lowestDisplay =
        lowest.transactionFeeNumeric === 0
          ? lowest.processingFeeNumeric === 0
            ? tConvenience('freeLowerCase', { defaultValue: 'free' })
            : lowest.processingFee
          : `${fixedFee}${lowest.transactionFee}`;

      const highestDisplay = `${fixedFee}${highest.transactionFee}`;

      return (
        <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
          {tConvenience(method === 'bank' ? 'feesVaryByPaymentRail' : 'feesVary', {
            lowestFee: lowestDisplay,
            highestFee: highestDisplay,
            defaultValue:
              method === 'bank'
                ? `Fees vary by payment rail (typically ${lowestDisplay} - ${highestDisplay} per transaction)`
                : `Fees vary (typically ${lowestDisplay} - ${highestDisplay} per transaction)`,
          })}
        </div>
      );
    }
  }

  // Original logic for non-flow 0/5 transactions
  // Determine delivery info
  const getDeliveryInfo = () => {
    // For bank, use banking rail description
    if (method === 'bank' && bankingPaymentRail) {
      return getBankingRailDescription(bankingPaymentRail);
    } else {
      return tBankingRail('instantDelivery', { defaultValue: 'Instant Delivery' });
    }

    return null;
  };

  const deliveryInfo = getDeliveryInfo();

  const feeBreakdown = showFees
    ? getProcessingFee({
        paymentMethod: method,
        province,
        bankingPaymentRail,
      })
    : null;

  const freeLabel = <div className={cn('freeBadge')}>{tConvenience('free', { defaultValue: 'Free' })}</div>;

  // Build fee content
  let feeContent = null;

  if (showFees) {
    if (feeBreakdown === null) {
      feeContent = freeLabel;
    } else {
      const { processingFee, transactionFee, processingFeeNumeric, transactionFeeNumeric } = feeBreakdown;

      // Both fees are 0 - show "Free"
      if (processingFeeNumeric === 0 && transactionFeeNumeric === 0) {
        feeContent = freeLabel;
      }
      // Only processing fee
      else if (processingFeeNumeric !== 0 && transactionFeeNumeric === 0) {
        feeContent = <>{processingFee}</>;
      }
      // Only transaction fee
      else if (processingFeeNumeric === 0 && transactionFeeNumeric !== 0) {
        feeContent = <>{transactionFee}</>;
      }
      // Both fees exist
      else {
        feeContent = <>{`${processingFee} + ${transactionFee}`}</>;
      }
    }
  }

  // If there's delivery info, show it
  if (deliveryInfo && !isNoTransactionFlow && showDeliveryTime) {
    return (
      <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>
        <span>{deliveryInfo}</span>

        {feeContent && (
          <>
            <span className={cn('spacer')}>{compact ? '·' : '•'}</span>
            {feeContent}
          </>
        )}
      </div>
    );
  }

  // No delivery info, just fees (or null if no fees to show)
  return feeContent && !isNoTransactionFlow ? (
    <div className={cn('deliveryAndFeeText', compact && 'compact', className)}>{feeContent}</div>
  ) : null;
}
